import { useMantineTheme } from '@mantine/core';
import { useEffect, useRef } from 'react';
import classes from './recording-waveform-scroller.module.css';

/** Ширина «свечки» (px). */
const BAR_WIDTH = 4;
/** Горизонтальный зазор между соседними столбцами (px). */
const GAP = 4;
/** Шаг сетки: ширина + зазор. */
const STEP = BAR_WIDTH + GAP;

/** Минимальная высота столбца при нулевой/очень малой амплитуде (px). */
const MIN_BAR_HEIGHT = 5;

/** Минимальный интервал между новыми столбцами (мс). Выше — медленнее «бегущая строка». */
const SAMPLE_INTERVAL_MS = 100;

/**
 * Сглаживание высоты нового столбца (0…1). Выше — мягче скачки амплитуды.
 * Компенсирует дискретные шаги `SAMPLE_INTERVAL_MS`.
 */
const AMPLITUDE_SMOOTHING = 0.32;

/** Усиление пика: выше — заметнее реакция на тихий звук (до клипа `Math.min(1, …)`). */
const AMPLITUDE_GAIN = 2.75;

/** Амплитуда предзаполненной «тишины» (высота задаётся `MIN_BAR_HEIGHT`). */
const SILENCE_LEVEL = 0;

/** Доля высоты контейнера под половину столбика (симметрично от центра). */
const BAR_HEIGHT_FRACTION = 0.42;

/** Смещение сырой амплитуды перед клипом (лучше видимость тихого сигнала). */
const AMPLITUDE_FLOOR = 0.2;

function getCanvas2dContext(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function peakFromByteTimeDomain(data: Uint8Array): number {
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    const v = Math.abs((data[i]! - 128) / 128);
    if (v > peak) {
      peak = v;
    }
  }
  return peak;
}

function clipNormalizedAmplitude(peak: number): number {
  return Math.min(1, peak * AMPLITUDE_GAIN + AMPLITUDE_FLOOR);
}

function exponentialSmooth(
  prev: number,
  target: number,
  smoothing: number
): number {
  return prev + (target - prev) * (1 - smoothing);
}

function scrollOffsetPx(time: number, lastSampleTime: number): number {
  return Math.min(STEP, ((time - lastSampleTime) / SAMPLE_INTERVAL_MS) * STEP);
}

function drawBars(
  ctx: CanvasRenderingContext2D,
  bars: readonly number[],
  width: number,
  height: number,
  barColor: string,
  scrollPx: number
): void {
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = barColor;

  const startX = width - bars.length * STEP - scrollPx;
  const midY = height / 2;
  const maxHalf = height * BAR_HEIGHT_FRACTION;

  for (let i = 0; i < bars.length; i++) {
    const amp = bars[i]!;
    const barH = Math.max(MIN_BAR_HEIGHT, amp * maxHalf * 2);
    const x = startX + i * STEP;
    const y = midY - barH / 2;
    const cornerR = Math.min(BAR_WIDTH / 2, barH / 2);
    ctx.beginPath();
    ctx.roundRect(x, y, BAR_WIDTH, barH, cornerR);
    ctx.fill();
  }
}

export type RecordingWaveformScrollerProps = {
  /** Активный поток микрофона */
  stream: MediaStream | null;
  /** Показывать анимацию (обычно `status === 'recording'`) */
  active: boolean;
};

/**
 * Бегущая полоса амплитуды (как в мессенджерах): новые столбики справа, слева уходят.
 * Использует `AnalyserNode` и отдельный `AudioContext` только для визуализации.
 */
export function RecordingWaveformScroller({
  stream,
  active,
}: RecordingWaveformScrollerProps) {
  const theme = useMantineTheme();
  const barColor = theme.colors.blue[6];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<number[]>([]);
  const rafRef = useRef(0);
  const lastSampleTimeRef = useRef(0);
  const smoothedAmpRef = useRef(0);

  useEffect(() => {
    if (!stream || !active) {
      historyRef.current = [];
      lastSampleTimeRef.current = 0;
      smoothedAmpRef.current = 0;
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (canvas && container) {
        const w = container.clientWidth;
        const h = container.clientHeight;
        const ctx = getCanvas2dContext(canvas, w, h);
        if (ctx) {
          ctx.clearRect(0, 0, w, h);
        }
      }
      return;
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.58;
    source.connect(analyser);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    void audioContext.resume();
    lastSampleTimeRef.current = 0;
    smoothedAmpRef.current = 0;

    let lastCanvasCssWidth = -1;
    let lastCanvasCssHeight = -1;
    let lastDevicePixelRatio = -1;

    const resolveContext = (
      canvas: HTMLCanvasElement,
      width: number,
      height: number
    ): CanvasRenderingContext2D | null => {
      const dpr = window.devicePixelRatio || 1;
      if (
        width !== lastCanvasCssWidth ||
        height !== lastCanvasCssHeight ||
        dpr !== lastDevicePixelRatio
      ) {
        lastCanvasCssWidth = width;
        lastCanvasCssHeight = height;
        lastDevicePixelRatio = dpr;
        return getCanvas2dContext(canvas, width, height);
      }
      return canvas.getContext('2d');
    };

    const draw = (time: number) => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      const width = container.clientWidth;
      const height = container.clientHeight;
      const maxBars = Math.max(1, Math.floor(width / STEP));

      if (historyRef.current.length === 0 && width > 0) {
        historyRef.current = Array.from(
          { length: maxBars },
          () => SILENCE_LEVEL
        );
        smoothedAmpRef.current = SILENCE_LEVEL;
        lastSampleTimeRef.current = time;
      }

      const shouldSample =
        lastSampleTimeRef.current === 0 ||
        time - lastSampleTimeRef.current >= SAMPLE_INTERVAL_MS;

      if (shouldSample) {
        lastSampleTimeRef.current = time;
        analyser.getByteTimeDomainData(dataArray);
        const peak = peakFromByteTimeDomain(dataArray);
        const raw = clipNormalizedAmplitude(peak);
        smoothedAmpRef.current = exponentialSmooth(
          smoothedAmpRef.current,
          raw,
          AMPLITUDE_SMOOTHING
        );
        historyRef.current.push(smoothedAmpRef.current);

        while (historyRef.current.length > maxBars) {
          historyRef.current.shift();
        }
      }

      const ctx = resolveContext(canvas, width, height);
      if (!ctx) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      const scrollPx = scrollOffsetPx(time, lastSampleTimeRef.current);
      drawBars(ctx, historyRef.current, width, height, barColor, scrollPx);

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close();
      historyRef.current = [];
      lastSampleTimeRef.current = 0;
      smoothedAmpRef.current = 0;
    };
  }, [stream, active, barColor]);

  return (
    <div ref={containerRef} className={classes.wrap} aria-hidden>
      <canvas ref={canvasRef} className={classes.canvas} />
    </div>
  );
}
